import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Segment,
  Label,
  Input,
  Icon,
  Popup,
  Button,
  Confirm,
  Image,
  Grid,
  GridRow,
  GridColumn,
} from "semantic-ui-react";
import { ENV, buildStorageUrl } from "../../../../../../../utils";
import { AudioUploader } from "../../../../../../Shared";
import { useDropzone } from "react-dropzone";
import { SlidesContextEdit } from "../../../../../../../contexts/MediaContext";
import "../TemplateManager.scss";

export const Smu03 = React.memo((props) => {
  const { index, slideId, onRemoveSlideByIndex } = props;
  const { slidesActiveSessionEdit } = useContext(SlidesContextEdit);
  const [showConfirm, setShowConfirm] = useState(false);
  const [title, setTitle] = useState("");
  const [titleAudio, setTitleAudio] = useState("");
  const [image, setImage] = useState("");
  const [audio, setAudio] = useState(null);
  const [titleAudio2, setTitleAudio2] = useState("");
  const [image2, setImage2] = useState("");
  const [audio2, setAudio2] = useState(null);

  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);

  /**
   * Synchronizes the component state with the content of the currently selected slide.
   *
   * This `useEffect` hook is executed whenever the `slideId` changes. It searches the `slidesActiveSessionEdit`
   * array for a slide that matches the `slideId`. If such a slide is found and it contains content, the hook
   * updates the component's state with the title, image, title audio, and audio from the first two audio objects
   * in the slide's content.
   *
   * Specifically, it performs the following actions:
   * - Sets the `title` state with the slide's title.
   * - Sets the `image` and `image2` states with the images from the first and second audio objects, respectively.
   * - Sets the `titleAudio` and `titleAudio2` states with the titles from the first and second audio objects, respectively.
   * - Sets the `audio` and `audio2` states with the audio files from the first and second audio objects, respectively.
   *
   * This ensures that the component accurately represents the content of the current slide, allowing for
   * effective editing and previewing within an active session.
   *
   * @dependency slideId - The ID of the current slide, changes to which trigger the hook.
   */
  useEffect(() => {
    const slide = slidesActiveSessionEdit.find(
      (slide) => slide._id === slideId
    );
    if (slide && slide.content) {
      setTitle(slide.content.title);
      setImage(slide.content.audios[0].image);
      setTitleAudio(slide.content.audios[0].title);
      setAudio(slide.content.audios[0].audio);
      setImage2(slide.content.audios[1].image);
      setTitleAudio2(slide.content.audios[1].title);
      setAudio2(slide.content.audios[1].audio);
    }
  }, [slideId]);

  /**
   * Updates the content of a specific slide within the `slidesActiveSessionEdit` array.
   *
   * This `useEffect` hook is triggered whenever there are changes to the `title`, `image`, `titleAudio`, `audio`, `image2`, `titleAudio2`, or `audio2` states. It begins by finding the index of the slide that matches the `slideId` within the `slidesActiveSessionEdit` array. If the slide is found (indicated by `slideIndex` not being -1), the hook updates the slide's content object with the new values for title, images, titles, and audios for the first two audio objects.
   *
   * Specifically, it performs the following updates:
   * - Sets the slide's title to the new `title`.
   * - Updates the first audio object's image, title, and audio file with `image`, `titleAudio`, and `audio` respectively.
   * - Updates the second audio object's image, title, and audio file with `image2`, `titleAudio2`, and `audio2` respectively.
   *
   * This ensures that any changes made to the slide's title or the first two audio objects are immediately reflected within the active session's slide array, maintaining the integrity and accuracy of the session content as it is being edited.
   *
   * @dependency title - The current title value to be updated for the slide.
   * @dependency image - The current image value for the first audio object.
   * @dependency titleAudio - The current title for the first audio object.
   * @dependency audio - The current audio file for the first audio object.
   * @dependency image2 - The current image value for the second audio object.
   * @dependency titleAudio2 - The current title for the second audio object.
   * @dependency audio2 - The current audio file for the second audio object.
   */
  useEffect(() => {
    const slideIndex = slidesActiveSessionEdit.findIndex(
      (slide) => slide._id === slideId
    );
    if (slideIndex !== -1) {
      slidesActiveSessionEdit[slideIndex].content.title = title;
      slidesActiveSessionEdit[slideIndex].content.audios[0].image = image;
      slidesActiveSessionEdit[slideIndex].content.audios[0].title = titleAudio;
      slidesActiveSessionEdit[slideIndex].content.audios[0].audio = audio;
      slidesActiveSessionEdit[slideIndex].content.audios[1].image = image2;
      slidesActiveSessionEdit[slideIndex].content.audios[1].title = titleAudio2;
      slidesActiveSessionEdit[slideIndex].content.audios[1].audio = audio2;
    }
  }, [title, image, titleAudio, audio, image2, titleAudio2, audio2]);

  /**
   * Event handlers for managing slide content updates and image uploads in Smu03.js.
   *
   * `handleTitleChange`:
   * - Listens for changes in the title input field.
   * - Updates the `title` state, trimming leading whitespace from the input value.
   *
   * `handleTitleAudioChange`:
   * - Listens for changes in the first title audio input field.
   * - Updates the `titleAudio` state, trimming leading whitespace from the input value.
   *
   * `handleTitleAudio2Change`:
   * - Listens for changes in the second title audio input field.
   * - Updates the `titleAudio2` state, trimming leading whitespace from the input value.
   *
   * `onDrop`:
   * - Handles file drops for the first image upload area.
   * - Accepts the first file from the dropped files, creates a URL object for it, and updates the `image` state.
   *
   * `onDrop2`:
   * - Handles file drops for the second image upload area.
   * - Similar to `onDrop`, but updates the `image2` state with the URL object of the accepted file.
   *
   * `useDropzone` configurations for `getRootProps1` and `getInputProps1`:
   * - Configures the first dropzone to accept JPEG and PNG images.
   * - Utilizes `onDrop` for handling file drops.
   *
   * `useDropzone` configurations for `getRootProps2` and `getInputProps2`:
   * - Configures the second dropzone similarly, but uses `onDrop2` for handling file drops.
   *
   * These handlers and configurations facilitate the dynamic updating of slide content, including title, title audios, and images, allowing for efficient editing within the component.
   */
  const handleTitleChange = (event) => {
    setTitle(event.target.value.trimStart());
  };

  const handleTitleAudioChange = (event) => {
    setTitleAudio(event.target.value.trimStart());
  };

  const handleTitleAudio2Change = (event) => {
    setTitleAudio2(event.target.value.trimStart());
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setImage(URL.createObjectURL(file));
  });

  const onDrop2 = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    setImage2(URL.createObjectURL(file));
  });

  const { getRootProps: getRootProps1, getInputProps: getInputProps1 } =
    useDropzone({
      accept: "image/jpeg, image/png",
      onDrop: onDrop,
    });

  const { getRootProps: getRootProps2, getInputProps: getInputProps2 } =
    useDropzone({
      accept: "image/jpeg, image/png",
      onDrop: onDrop2,
    });

  return (
    <>
      <Segment raised className="media-slide">
        <div className="media-slide__header">
          <div className="media-slide__header">
            <Label attached="top left">
              Hoja {index + 1} de {slidesActiveSessionEdit.length}
            </Label>
          </div>
          <div className="media-slide__button">
            <Popup
              content="Eliminar hoja"
              trigger={
                <Button
                  compact
                  color="grey"
                  icon="trash alternate outline"
                  onClick={onOpenCloseConfirm}
                />
              }
            />
          </div>
        </div>
        <div className="media-slide__input">
          <div className="media-slide__label-div">
            <Icon name="bookmark" />
            <h5 className="media-slide__label">Título:</h5>
          </div>
          <Input
            fluid
            className="media-slide__input-title"
            name="title"
            value={title}
            onChange={handleTitleChange}
          />
        </div>
        <div className="row">
          <div className="media-slide__label-div">
            <Icon name="play circle outline" />
            <h5 className="media-slide__label">Contenido:</h5>
          </div>

          <Grid columns={2} divided>
            <GridRow>
              <GridColumn>
                <div className="media-slide__image-audio" {...getRootProps1()}>
                  <input {...getInputProps1()} />
                  <Image
                    src={image?.startsWith('blob:') ? image : (buildStorageUrl(image) || image)}
                    rounded
                    fluid
                  />
                </div>
                <div className="media-slide__label-div">
                  <h5 className="media-slide__label">Título del audio:</h5>
                </div>
                <Input
                  fluid
                  className="media-slide__input"
                  name="titleAudio"
                  value={titleAudio}
                  onChange={handleTitleAudioChange}
                />
                <AudioUploader audio={audio} setAudio={setAudio} />
              </GridColumn>
              <GridColumn>
                <div className="media-slide__image-audio" {...getRootProps2()}>
                  <input {...getInputProps2()} />
                  <Image
                    src={image2?.startsWith('blob:') ? image2 : (buildStorageUrl(image2) || image2)}
                    rounded
                    fluid
                  />
                </div>
                <div className="media-slide__label-div">
                  <h5 className="media-slide__label">Título del audio:</h5>
                </div>
                <Input
                  fluid
                  className="media-slide__input"
                  name="titleAudio"
                  value={titleAudio2}
                  onChange={handleTitleAudio2Change}
                />
                <AudioUploader audio={audio2} setAudio={setAudio2} />
              </GridColumn>
            </GridRow>
          </Grid>
        </div>
      </Segment>
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={() => onRemoveSlideByIndex(index)}
        content={`¿Deseas eliminar esta hoja?`}
        size="mini"
      />
    </>
  );
});
